import React, { useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

/**
 * Global Lab kill-switch bar. When tripped, the whole Lab halts new entries
 * (and optionally flattens open positions). Reset re-enables the Lab.
 */
const KillSwitchBar: React.FC = () => {
  const killSwitch = useStrategyLabStore((s) => s.killSwitch);
  const loading = useStrategyLabStore((s) => s.killSwitchLoading);
  const fetchKillSwitch = useStrategyLabStore((s) => s.fetchKillSwitch);
  const activateKillSwitch = useStrategyLabStore((s) => s.activateKillSwitch);
  const resetKillSwitch = useStrategyLabStore((s) => s.resetKillSwitch);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closePositions, setClosePositions] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchKillSwitch();
    const interval = setInterval(fetchKillSwitch, 60_000);
    return () => clearInterval(interval);
  }, [fetchKillSwitch]);

  const enabled = killSwitch?.lab_enabled ?? true;
  const activeDeployments = killSwitch?.active_deployments ?? 0;

  const handleTrip = async () => {
    setBusy(true);
    await activateKillSwitch(closePositions);
    setBusy(false);
    setConfirmOpen(false);
  };

  const handleReset = async () => {
    setBusy(true);
    await resetKillSwitch();
    setBusy(false);
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
        enabled ? 'border-border bg-card' : 'border-rose-500/40 bg-rose-500/10'
      }`}
    >
      <div className="flex items-center gap-3">
        {enabled ? (
          <ShieldCheck className="w-5 h-5 text-emerald-500" />
        ) : (
          <ShieldAlert className="w-5 h-5 text-rose-500" />
        )}
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">Lab Kill Switch</span>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            {enabled ? (
              <Badge variant="success" className="text-[10px]">
                Lab active
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-[10px]">
                Halted
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {activeDeployments} active deployment{activeDeployments === 1 ? '' : 's'}
            {enabled ? '' : ' — new entries blocked'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {enabled ? (
          <Button variant="destructive" size="sm" onClick={() => setConfirmOpen(true)} disabled={busy}>
            <ShieldAlert className="w-4 h-4" /> Trip kill switch
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={handleReset} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Reset &amp; re-enable
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trip the Lab kill switch?</DialogTitle>
            <DialogDescription>
              This halts all new Strategy Lab entries immediately across every deployment.
              Reset to re-enable.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <Label className="text-sm font-medium">Close open positions</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Flatten all open Lab positions on the paper account as part of the halt.
              </p>
            </div>
            <Switch checked={closePositions} onCheckedChange={setClosePositions} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleTrip} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
              Trip kill switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KillSwitchBar;
